// 2-D Dynamic Programming — curated interview problems with multiple worked approaches.
// Same shape as dsaProblems/dp1d.js: concise steps, a "why & when to use" close per
// approach, and an interactive visualizer using the multi-row array convention for grids.

const dp2dProblems = [
  {
    id: 'unique-paths',
    title: 'Unique Paths',
    difficulty: 'Medium',
    leetcode: { number: 62, url: 'https://leetcode.com/problems/unique-paths/' },
    statement: 'A robot sits at the top-left corner of an m×n grid. It can only move right or down at each step. How many distinct paths exist to reach the bottom-right corner?',
    example: {
      input: 'm = 3, n = 3',
      output: '6',
      note: 'Six distinct sequences of right/down moves reach the bottom-right corner of a 3×3 grid.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force Recursion',
        why:
          "From any cell, recursively try moving right and moving down, and sum the path counts from both. " +
          "Correct — this explores every possible route directly — but the same cell gets reached, and fully " +
          "recomputed, by many different earlier paths that both lead through it.",
        steps: [
          'Base case: reaching the bottom-right cell is exactly 1 way (arriving there IS success).',
          'If out of bounds, contribute 0 paths.',
          'Otherwise, recursively sum the paths from moving down and the paths from moving right.',
          'The same (row, col) pair is revisited by many different call paths, each re-deriving it from scratch.',
        ],
        code:
`int uniquePathsBruteForce(int m, int n, int i, int j) {
    if (i == m - 1 && j == n - 1) return 1;
    if (i >= m || j >= n) return 0;
    return uniquePathsBruteForce(m, n, i + 1, j) + uniquePathsBruteForce(m, n, i, j + 1);
}`,
        complexity: { time: 'O(2^(m+n))', space: 'O(m + n) — recursion depth' },
        whenToUse: "Only useful to confirm the recurrence directly — the same cell is reached (and recomputed) via many overlapping paths, which is exactly what the DP version below eliminates.",
      },
      {
        name: 'Approach 2 — Optimized: Bottom-Up Grid DP',
        why:
          "Build dp[i][j] — the number of distinct routes to reach cell (i,j) — forward, row by row. Every cell " +
          "only ever needs the cell directly above and the cell directly to the left, both already finished by " +
          "the time it's computed.",
        steps: [
          'Every cell along the top row or left column has exactly 1 route — a straight line of moves.',
          'For every other cell, dp[i][j] = dp[i-1][j] + dp[i][j-1] — every route through either neighbor is a distinct, valid route to (i,j) itself.',
          'Fill the grid top-left to bottom-right, one row at a time.',
          'Return dp[m-1][n-1].',
        ],
        visual: {
          arrays: [
            { label: 'row 0', values: [1, 1, 1] },
            { label: 'row 1', values: [1, null, null] },
            { label: 'row 2', values: [1, null, null] },
          ],
          varNames: [],
          steps: [
            { note: 'Row 0 and column 0 are all 1 — only one way to reach any edge cell (always right along the top, always down along the left).' },
            { snapshots: { 'row 1': [1, 2, null] }, highlights: { 'row 1': [1] }, note: 'dp[1][1] = dp[0][1] + dp[1][0] = 1 + 1 = 2.' },
            { snapshots: { 'row 1': [1, 2, 3] }, highlights: { 'row 1': [2] }, note: 'dp[1][2] = dp[0][2] + dp[1][1] = 1 + 2 = 3.' },
            { snapshots: { 'row 2': [1, 3, null] }, highlights: { 'row 2': [1] }, note: 'dp[2][1] = dp[1][1] + dp[2][0] = 2 + 1 = 3.' },
            { snapshots: { 'row 2': [1, 3, 6] }, highlights: { 'row 2': [2] }, note: 'dp[2][2] = dp[1][2] + dp[2][1] = 3 + 3 = 6 — the bottom-right corner, and the final answer.' },
          ],
        },
        code:
`int uniquePaths(int m, int n) {
    int[][] dp = new int[m][n];
    for (int i = 0; i < m; i++) {
        for (int j = 0; j < n; j++) {
            dp[i][j] = (i == 0 || j == 0) ? 1 : dp[i - 1][j] + dp[i][j - 1];
        }
    }
    return dp[m - 1][n - 1];
}`,
        complexity: { time: 'O(m · n)', space: 'O(m · n) — or O(n) with a rolling row, since row i only reads row i-1' },
        interviewNote: "Try the \"Unique Paths\" mode in this pattern's interactive playground with any grid up to 5×5.",
        whenToUse: "The standard, easiest-to-derive correct solution — always safe to lead with, and the row-compression follow-up (O(n) space) is a natural next step to mention.",
      },
      {
        name: 'Approach 3 — Further Optimized: Combinatorics (Math Formula)',
        why:
          "Reaching the bottom-right corner always takes exactly (m-1) down-moves and (n-1) right-moves, in SOME " +
          "order — (m+n-2) total moves. Choosing which of those moves are \"down\" (or equivalently, which are " +
          "\"right\") is a pure combinations question: C(m+n-2, m-1), computed directly with no grid at all.",
        steps: [
          'The total number of moves is fixed: (m-1) down-moves plus (n-1) right-moves = m+n-2 moves.',
          'Every distinct ORDERING of those moves is a distinct path — so the answer is "choose which m-1 of the m+n-2 moves are down-moves": C(m+n-2, m-1).',
          'Compute the binomial coefficient iteratively, multiplying and dividing one term at a time to avoid overflow.',
          'Return the result directly — no grid, no recursion.',
        ],
        code:
`long uniquePathsMath(int m, int n) {
    long result = 1;
    int totalMoves = m + n - 2;
    int r = Math.min(m, n) - 1;
    for (int i = 1; i <= r; i++) {
        result = result * (totalMoves - r + i) / i;
    }
    return result;
}`,
        complexity: { time: 'O(min(m, n))', space: 'O(1)' },
        interviewNote: "A great follow-up to mention after the DP solution — it shows the same problem can sometimes be solved directly with combinatorics once the grid's structure is recognized as \"count the orderings of a fixed multiset of moves.\"",
        whenToUse: "Worth deriving out loud as the theoretically fastest approach — but the bottom-up DP (Approach 2) is usually still the expected primary answer in an interview, since not every grid-DP problem has a clean closed-form shortcut like this one.",
      },
    ],
  },
  {
    id: 'longest-common-subsequence',
    title: 'Longest Common Subsequence',
    difficulty: 'Medium',
    leetcode: { number: 1143, url: 'https://leetcode.com/problems/longest-common-subsequence/' },
    statement: 'Given two strings, return the length of their longest common subsequence — characters that appear in both strings, in the same relative order, but not necessarily contiguous.',
    example: {
      input: 'text1 = "abcde", text2 = "ace"',
      output: '3',
      note: '"ace" is a subsequence of both strings — the longest one they share.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force Recursion',
        why:
          "Walk two pointers, one into each string. If the characters they point at match, they must both " +
          "belong to SOME optimal subsequence, so advance both together. Otherwise, try advancing just one " +
          "pointer, then just the other, and keep the better result. Correct, but \"the LCS of these two exact " +
          "SUFFIXES\" gets independently re-solved by every different earlier decision that reaches the same pair.",
        steps: [
          'Base case: either string running out means no more characters can be matched — contribute 0.',
          'If the characters at the current positions match, they belong to the LCS — advance both pointers, adding 1.',
          'Otherwise, try advancing just the first pointer, and separately just the second, taking whichever gives a longer result.',
          'The same (i, j) pair of positions recurs constantly across different branches.',
        ],
        code:
`int lcsBruteForce(String text1, String text2, int i, int j) {
    if (i == text1.length() || j == text2.length()) return 0;
    if (text1.charAt(i) == text2.charAt(j)) {
        return 1 + lcsBruteForce(text1, text2, i + 1, j + 1);
    }
    return Math.max(
        lcsBruteForce(text1, text2, i + 1, j),
        lcsBruteForce(text1, text2, i, j + 1)
    );
}`,
        complexity: { time: 'O(2^(m+n))', space: 'O(m + n) — recursion depth' },
        whenToUse: "Only useful to confirm the match/no-match framing — without memoizing on the (i, j) pair, overlapping suffixes get re-solved repeatedly.",
      },
      {
        name: 'Approach 2 — Optimized: Bottom-Up Grid DP',
        why:
          "Build dp[i][j] — the LCS length using only the first i characters of text1 and the first j characters " +
          "of text2 — forward. A character match reads the DIAGONAL neighbor (both prefixes were one step " +
          "shorter); a mismatch takes the better of dropping the last character of either string.",
        steps: [
          'dp[0][j] and dp[i][0] are always 0 — an empty prefix shares nothing with anything.',
          'If text1[i-1] equals text2[j-1], dp[i][j] = dp[i-1][j-1] + 1 — extend the diagonal\'s best match by one.',
          'Otherwise, dp[i][j] = max(dp[i-1][j], dp[i][j-1]) — take whichever neighbor (drop a character from either string) is better.',
          'Return dp[m][n].',
        ],
        visual: {
          arrays: [
            { label: 'row 0 (text1="abc", text2="ac")', values: [0, 0, 0] },
            { label: 'row 1 (text1[0]=\'a\')', values: [0, 1, null] },
            { label: 'row 2 (text1[1]=\'b\')', values: [0, 1, 1] },
          ],
          varNames: [],
          steps: [
            { note: 'Row 0 and column 0 are all 0 — no characters compared yet. dp[1][1]: text1[0]=\'a\' == text2[0]=\'a\' → match, dp[1][1] = dp[0][0] + 1 = 1.' },
            { snapshots: { 'row 1 (text1[0]=\'a\')': [0, 1, 1] }, highlights: { 'row 1 (text1[0]=\'a\')': [2] }, note: '\'a\' ≠ \'c\': dp[1][2] = max(dp[0][2], dp[1][1]) = max(0, 1) = 1.' },
            { snapshots: { 'row 2 (text1[1]=\'b\')': [0, 1, 1] }, highlights: { 'row 2 (text1[1]=\'b\')': [1] }, note: '\'b\' ≠ \'a\': dp[2][1] = max(dp[1][1], dp[2][0]) = max(1, 0) = 1.' },
            { note: 'Continuing this way to row 3 (text1[2]=\'c\'), \'c\' matches text2[1]=\'c\' at dp[3][2] = dp[2][1] + 1 = 2 — the final LCS length for "abc" vs "ac" is 2 ("ac"). For the full example text1="abcde", text2="ace", the same process yields 3 ("ace").' },
          ],
        },
        code:
`int longestCommonSubsequence(String text1, String text2) {
    int m = text1.length(), n = text2.length();
    int[][] dp = new int[m + 1][n + 1];
    for (int i = 1; i <= m; i++) {
        for (int j = 1; j <= n; j++) {
            if (text1.charAt(i - 1) == text2.charAt(j - 1)) {
                dp[i][j] = dp[i - 1][j - 1] + 1;
            } else {
                dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
            }
        }
    }
    return dp[m][n];
}`,
        complexity: { time: 'O(m · n)', space: 'O(m · n) — or O(min(m, n)) with a rolling row' },
        interviewNote: "Try the \"Longest Common Subseq.\" mode in this pattern's interactive playground with any two short strings to watch this exact grid fill in, including the diagonal-match highlighting.",
        whenToUse: "The template for every \"align two sequences\" 2-D DP problem — Edit Distance, Longest Common Substring, and Interleaving String all reuse this exact match/mismatch grid shape, each with a small twist on the combining rule.",
      },
    ],
  },
];

export default dp2dProblems;
